import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building,
  Building2,
  Home as HomeIcon,
  Users,
  HeartHandshake,
  ShoppingBag,
  Cog,
  Wrench,
  School,
  Target,
  Timer,
  Quote
} from 'lucide-react';
import './Home.css';

const Home = () => {
  const stats = [
    { number: '100%', label: 'Customer Retention', icon: HeartHandshake },
    { number: '<5mins', label: 'Average Response Time', icon: Timer },
    { number: '>90%', label: 'First Call Resolution Rate', icon: Cog },
    { number: '98%', label: 'Project on-time delivery', icon: Target }
  ];

  const features = [
    {
      id: '01',
      title: 'Technical Support',
      description:
        'We provide direct access to senior Level 3 technicians, ensuring your issues are handled by experts from the first touch. Our team is built for speed, rapidly acknowledging and resolving problems so your operations never stall. With deep experience across diverse platforms, applications, networks, and hybrid environments, we deliver comprehensive support that keeps every part of your technology ecosystem stable and reliable.',
      icon: '01'
    },
    {
      id: '02',
      title: 'Automation and Integration',
      description:
        'We build custom automation and integration solutions that connect your systems and eliminate the manual work slowing your business down. Our team designs and develops tailored workflows, data pipelines, and applications that streamline operations and improve accuracy across your entire organization. By unifying platforms and optimizing how information moves, we create a technology foundation that supports efficiency, scalability, and long-term growth.',
      icon: '02'
    },
    {
      id: '03',
      title: 'Web Design and Hosting',
      description:
        'We create modern, high-performance websites tailored to the way businesses operate and grow. We design clean, intuitive interfaces backed by solid architecture, ensuring your site runs fast, looks professional, and represents your brand with credibility. With secure, fully managed hosting and ongoing technical oversight, we keep your website stable, optimized, and ready for whatever your business needs next.',
      icon: '03'
    },
    {
      id: '04',
      title: 'Backup and Security',
      description:
        'We safeguard your business with secure cloud backups that keep your critical data protected and always recoverable. We combine advanced endpoint and email security to block threats before they disrupt your operations or compromise sensitive information. By pairing reliable backup strategies with proactive defense, we ensure your systems stay resilient, secure, and ready to withstand whatever the digital world throws at them.',
      icon: '04'
    }
  ];

  const testimonials = [
  {
    id: 1,
    client: "Mary Macleod's Shortbread",
    companyUrl: 'https://www.marymacleod.com',
    author: 'Sharon Macleod',
    date: 'October 1, 2025',
    quote:
      'Partnering with Snap Remedy over the past five+ years has transformed the way we operate. They’ve played a critical role in modernizing our business by building powerful system integrations that connect our internal platforms, like our ERP, with external systems—eliminating manual data entry and significantly reducing errors.',
    caseStudyId: 1,
    logo: '/logo-mms.jpg',
    logoAlt: "Mary Macleod's Shortbread logo"
  },
  {
    id: 2,
    client: 'Pop Sandbox',
    companyUrl: 'https://popsandbox.com',
    author: 'Alex Jansen',
    date: 'January 20, 2026',
    quote:
      'Working with Snap Remedy over the past seven-plus years has been a game-changer for our team. As a media company, reliable hosting and responsive technical support are critical to everything we do, and Snap Remedy has consistently delivered on both fronts.',
    caseStudyId: 8,
    logo: '/logo-ps.jpg',
    logoAlt: 'Pop Sandbox logo'
  },
  {
    id: 3,
    client: 'Full Voice Music',
    companyUrl: 'https://fullvoicemusic.com',
    author: 'Nikki Loney',
    date: 'July 14, 2025',
    quote:
      'After working with several other website developers, we are thrilled to partner with Jason and the team at Snap Remedy. Jason truly listens to our goals, meets timelines, and makes us feel genuinely included in the design process while offering innovative, up-to-date strategies.',
    caseStudyId: 9,
    logo: '/logo-fvm.png',
    logoAlt: 'Full Voice Music logo'
  },
  {
    id: 4,
    client: 'Aeras Water',
    companyUrl: 'https://aeraswater.com',
    author: 'Aeras Water Team',
    date: 'Add date',
    quote:
      'Snap Remedy helped strengthen our security posture with responsive support and proactive monitoring that gave our growing team confidence.',
    caseStudyId: 3,
    logo: '/logo-aw.jpg',
    logoAlt: 'Aeras Water logo'
  },
  {
    id: 5,
    client: 'Universal Staffing',
    companyUrl: 'https://universalstaffing.ca',
    author: 'Universal Staffing Team',
    date: 'Add date',
    quote:
      'Their fast response times and dependable technical support have helped keep our multi-location operation running without interruption.',
    caseStudyId: 5,
    logo: '/logo-usi.svg',
    logoAlt: 'Universal Staffing logo'
  },
  {
    id: 6,
    client: "Federation of Metro Tenants' Association",
    companyUrl: 'https://www.torontotenants.org',
    author: 'FMTA Team',
    date: 'Add date',
    quote:
      'Snap Remedy turned a difficult paper-based process into a searchable, secure system that made daily work dramatically easier for staff.',
    caseStudyId: 4,
    logo: '/fmta-logo.png',
    logoAlt: "Federation of Metro Tenants' Association logo"
  }
];

  const industries = [
    {
      name: 'Professional Services',
      slug: 'professional-services',
      Icon: Building
    },
    {
      name: 'Manufacturing & Fabrication',
      slug: 'manufacturing-fabrication',
      Icon: Building2
    },
    {
      name: 'Retail',
      slug: 'retail',
      Icon: ShoppingBag
    },
    {
      name: 'Property Management & Real Estate',
      slug: 'property-management-real-estate-groups',
      Icon: HomeIcon
    },
    {
      name: 'Non-profits / Unions',
      slug: 'non-profits-unions',
      Icon: Users
    },
    {
      name: 'Education & Community Services',
      slug: 'education-community-services',
      Icon: School
    },
    {
      name: 'Construction & Trades',
      slug: 'construction-trades',
      Icon: Wrench
    }
  ];

  return (
    <div className="home">
      <section className="hero">
        <div className="hero-container">
          <motion.div className="hero-content">
            <div className="hero-label">
              <span className="label-dot"></span>
              Empower Your Business
            </div>

            <h1 className="hero-title">
              Let Our Team of Experts
              <span className="highlight">Be Your Team of Experts</span>
            </h1>

            <p className="hero-description">
              Your business depends on technology that delivers reliable performance every day. Snap Remedy provides the technical support, optimization, security, and automated solutions companies need to stay efficient and protected. With experienced guidance and a proven approach, we make sure your technology is secure, efficient, and ready for whatever your operations demand.
            </p>

            <div className="hero-actions">
              <Link to="/contact" className="btn-hero-primary">
                Lets get started
                <span className="btn-arrow">→</span>
              </Link>

              <Link to="/solutions" className="btn-hero-secondary">
                Explore Solutions
              </Link>
            </div>
          </motion.div>

          <img
            src="hero-snap-remedy-homepage.svg"
            className="hero-img"
            alt="Illustration of a computer, phone, chip, and a coding prompt"
          />
        </div>
      </section>

      <section className="stats section-spacing">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div key={index} className="stat-card">
                  <div className="stat-icon">
                    <Icon size={20} aria-hidden="true" />
                  </div>
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                  <div className="stat-bar"></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="features section-spacing">
        <div className="container">
          <motion.div className="section-header">
            <h2 className="section-title">Business Solutions</h2>
            <p className="section-description">
              Snap Remedy delivers expert-managed IT services that help companies strengthen their technology and operate with confidence. Whether you're gearing up for growth or refining what you already have, our team provides the support, guidance, and solutions to move your business forward.
            </p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature) => (
              <motion.div key={feature.id} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <Link
                  to="/solutions"
                  state={{ solutionId: feature.id }}
                  className="feature-link"
                >
                  Learn more →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="industries section-spacing">
        <div className="container">
          <motion.div className="section-header">
            <h2 className="section-title">Industries We Serve</h2>
            <p className="section-description">
              Businesses in every industry depend on reliable technology, and Snap Remedy delivers solutions that adapt to the unique demands and growing pains of each one. From operations to communications to data management, we step in as your dedicated I.T. team, handling the complexity so you can focus on running your business. With us as your partner, you gain the expertise and stability needed to keep your organization moving confidently forward.
            </p>
          </motion.div>

          <div className="industries-grid">
            {industries.map((industry, index) => {
              const IndustryIcon = industry.Icon;

              return (
                <motion.div key={index} className="industry-card">
                  <Link
                    to={`/industries?industry=${industry.slug}#industry-explorer`}
                    className="industry-link"
                  >
                    <span className="industry-icon">
                      <IndustryIcon size={20} aria-hidden="true" />
                    </span>
                    <span className="industry-name">{industry.name}</span>
                    <span className="industry-arrow">→</span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="testimonials section-spacing">
        <div className="container">
          <motion.div className="section-header">
            <h2 className="section-title">Testimonials</h2>
            <p className="section-description">
              Businesses across every industry count on Snap Remedy, and our clients’ testimonials tell the story best. From streamlined operations to stronger communication and smarter data management, they share how partnering with our team transformed their technology challenges into reliable, lasting solutions.
            </p>
          </motion.div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial) => (
              <motion.article key={testimonial.id} className="testimonial-card">
                <div className="testimonial-quote-icon">
                  <Quote size={20} aria-hidden="true" />
                </div>

                <p className="testimonial-quote">“{testimonial.quote}”</p>

                <div className="testimonial-meta">
                  <div className="testimonial-meta-text">
                    <div className="testimonial-author">{testimonial.author}</div>

                    {testimonial.companyUrl ? (
                      <a
                        href={testimonial.companyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="testimonial-company testimonial-company-link"
                      >
                        {testimonial.client}
                      </a>
                    ) : (
                      <div className="testimonial-company">{testimonial.client}</div>
                    )}

                    <div className="testimonial-date">{testimonial.date}</div>
                  </div>

                  {testimonial.logo && (
                    <div className="testimonial-logo-wrap">
                      <img
                        src={testimonial.logo}
                        alt={testimonial.logoAlt || `${testimonial.client} logo`}
                        className="testimonial-logo"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>

                <Link
                  to={`/case-studies#case-study-${testimonial.caseStudyId}`}
                  className="testimonial-link"
                >
                  Read more →
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="cta section-spacing">
        <div className="container">
          <motion.div className="cta-content">
            <h2 className="cta-title">Don't See Your Industry?</h2>
            <p className="cta-description">
              We build solutions across a wide spectrum of industries, and the examples we highlight are just a glimpse of our reach. If your field isn’t listed, you can still count on the same depth of expertise and a solution shaped precisely for your business. Our focus is delivering what works best for you, whatever your industry may be.
            </p>
            <div className="cta-actions">
              <Link to="/contact" className="btn-cta-primary">
                Get Started Today
              </Link>
              <Link to="/case-studies" className="btn-cta-secondary">
                View Case Studies
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;